import React, { useState, useCallback } from 'react';
import { Search, Download, Eye, UserPlus } from 'lucide-react';
import { useUsers, useGrantSubscription } from '../hooks/useUsers';
import Card from '../components/ui/Card';
import Badge, { statusVariant } from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Pagination from '../components/ui/Pagination';
import Modal from '../components/ui/Modal';
import { TableSkeleton } from '../components/ui/Skeleton';
import UserDetail from './UserDetail';
import { formatDate } from '../utils/formatters';
import type { User } from '../types';

export default function UsersPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [grantModal, setGrantModal] = useState<User | null>(null);
  const [grantDays, setGrantDays] = useState(30);

  const { data, isLoading } = useUsers({
    page,
    limit: 20,
    search: debouncedSearch,
    status: statusFilter || undefined,
  });

  const grantSub = useGrantSubscription();

  // Debounced search
  const debounceRef = React.useRef<NodeJS.Timeout>(undefined);
  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  }, []);

  const handleExportCSV = () => {
    // Simple CSV export from current data
    if (!data?.data) return;
    const headers = 'Phone,Name,Status,Joined\n';
    const rows = data.data
      .map((u) => `${u.phone},${u.name || ''},${u.subscriptionStatus},${formatDate(u.createdAt)}`)
      .join('\n');
    const blob = new Blob([headers + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="space-y-4">
        {/* Top Bar */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-text-primary">Users</h2>
            {data && (
              <Badge variant="info" size="sm">
                {data.total}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-hint" />
              <input
                type="text"
                placeholder="Search by phone..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9 pr-4 py-2 bg-surface border-0 rounded-btn text-sm text-text-primary placeholder:text-text-hint focus:ring-2 focus:ring-primary/20 w-52"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="bg-surface border-0 rounded-btn px-3 py-2 text-sm text-text-primary focus:ring-2 focus:ring-primary/20"
            >
              <option value="">All Status</option>
              <option value="TRIAL">Trial</option>
              <option value="ACTIVE">Active</option>
              <option value="EXPIRED">Expired</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
            <Button variant="secondary" size="sm" icon={<Download className="w-4 h-4" />} onClick={handleExportCSV}>
              Export
            </Button>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <TableSkeleton rows={8} />
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-surface">
                    {['Phone', 'Name', 'Status', 'Trial/Sub Ends', 'Joined', 'Actions'].map(
                      (h) => (
                        <th
                          key={h}
                          className="px-5 py-3 text-left text-[11px] font-semibold text-text-hint uppercase tracking-wider"
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(data?.data || []).map((user) => (
                    <tr
                      key={user.id}
                      className="border-b border-border/50 hover:bg-surface/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedUser(user)}
                    >
                      <td className="px-5 py-4 text-sm font-medium text-text-primary">
                        {user.phone}
                      </td>
                      <td className="px-5 py-4 text-sm text-text-secondary">
                        {user.name || '—'}
                      </td>
                      <td className="px-5 py-4">
                        <Badge variant={statusVariant(user.subscriptionStatus)}>
                          {user.subscriptionStatus}
                        </Badge>
                      </td>
                      <td className="px-5 py-4 text-sm text-text-secondary">
                        {user.subscriptionEndDate
                          ? formatDate(user.subscriptionEndDate)
                          : user.trialEndDate
                          ? formatDate(user.trialEndDate)
                          : '—'}
                      </td>
                      <td className="px-5 py-4 text-sm text-text-secondary">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => setSelectedUser(user)}
                            className="p-1.5 rounded-lg hover:bg-surface text-text-hint hover:text-primary transition-colors"
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setGrantModal(user)}
                            className="p-1.5 rounded-lg hover:bg-surface text-text-hint hover:text-primary transition-colors"
                            title="Grant subscription"
                          >
                            <UserPlus className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data && data.totalPages > 1 && (
              <Pagination
                page={page}
                totalPages={data.totalPages}
                total={data.total}
                limit={data.limit}
                onPageChange={setPage}
              />
            )}
          </Card>
        )}
      </div>

      {/* User Detail Drawer */}
      {selectedUser && (
        <UserDetail user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}

      {/* Grant Subscription Modal */}
      <Modal
        open={!!grantModal}
        onClose={() => setGrantModal(null)}
        title="Grant Subscription"
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            Grant subscription access to <strong>{grantModal?.phone}</strong>
          </p>
          <Input
            label="Number of days"
            type="number"
            min={1}
            max={365}
            value={grantDays}
            onChange={(e) => setGrantDays(Number(e.target.value))}
          />
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setGrantModal(null)}>
              Cancel
            </Button>
            <Button
              loading={grantSub.isPending}
              onClick={() => {
                if (grantModal) {
                  grantSub.mutate(
                    { id: grantModal.id, days: grantDays },
                    { onSuccess: () => setGrantModal(null) }
                  );
                }
              }}
            >
              Grant Access
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
